import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Package, Search, Edit, Trash2, Tag, Barcode, DollarSign, Settings, Printer } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import ProductForm from './ProductForm';
import CategoriesTab from './CategoriesTab';
import ImportProductsModal from './ImportProductsModal';
import PrinterAssignmentModal from './PrinterAssignmentModal';
import apiService from '../../services/apiService';
import { formatMoney } from '../../utils/formatters';
import './ProductManagementPage.css';

const ITEMS_PER_LOAD = 50;

const ProductManagementPage = () => {
    const { setError, clearError } = useApp();
    const [loading, setLoading] = useState(true);
    const searchInputRef = useRef(null);
    const tableContainerRef = useRef(null);

    const [activeTab, setActiveTab] = useState('products');
    const [productos, setProductos] = useState([]);
    const [filteredProductos, setFilteredProductos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [almacenes, setAlmacenes] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showPrinterModal, setShowPrinterModal] = useState(false);
    const [selectedProducto, setSelectedProducto] = useState(null);
    const [headerToggle, setHeaderToggle] = useState(null);
    const [visibleCount, setVisibleCount] = useState(ITEMS_PER_LOAD);
    const [soloMayorEquivalencia, setSoloMayorEquivalencia] = useState(true);

    // Filtros por columna
    const [columnFilters, setColumnFilters] = useState({
        nombre: '',
        codigo_corto: '',
        codigo_barras: '',
        categoria: '',
        almacen: ''
    });

    useEffect(() => {
        loadProductos();
        loadCategorias();
        loadAlmacenes();
    }, []);

    useEffect(() => {
        if (activeTab === 'products' && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [activeTab]);

    useEffect(() => {
        filterProductos();
    }, [productos, searchQuery, selectedCategory, columnFilters, soloMayorEquivalencia]);

    const handleColumnFilterChange = (column, value) => {
        setColumnFilters(prev => ({
            ...prev,
            [column]: value
        }));
    };

    const clearAllFilters = () => {
        setSearchQuery('');
        setSelectedCategory('');
        setColumnFilters({
            nombre: '',
            codigo_corto: '',
            codigo_barras: '',
            categoria: '',
            almacen: ''
        });
    };

    const hasActiveFilters = searchQuery || selectedCategory ||
        Object.values(columnFilters).some(f => f && f.trim() !== '');

    const loadProductos = async () => {
        try {
            setLoading(true);
            clearError();

            const result = await apiService.getProductos();

            if (result.success) {
                setProductos(result.data);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al cargar productos');
        } finally {
            setLoading(false);
        }
    };

    const loadCategorias = async () => {
        try {
            const result = await apiService.getCategorias();
            if (result.success) {
                setCategorias(result.data);
            }
        } catch (error) {
            console.error('Error al cargar categorías:', error);
        }
    };

    const loadAlmacenes = async () => {
        try {
            const result = await apiService.getAlmacenes();
            if (result.success) {
                setAlmacenes(result.data);
            }
        } catch (error) {
            console.error('Error al cargar almacenes:', error);
        }
    };

    // Normalizar texto: quitar # y ceros a la izquierda de números
    const normalizarTexto = (txt) => (txt || '').toLowerCase().replace(/#\s*/g, '').replace(/\b0+(\d)/g, '$1');

    // Verificar si todas las palabras del filtro están en el texto (con normalización)
    const coincideNormalizado = (texto, filtro) => {
        const textoNorm = normalizarTexto(texto);
        const palabras = normalizarTexto(filtro).split(/\s+/);
        return palabras.every(w => textoNorm.includes(w));
    };

    const filterProductos = () => {
        let filtered = productos;

        // Filtro de mayor equivalencia (ocultar productos base que tienen equivalencia)
        if (soloMayorEquivalencia) {
            filtered = filtered.filter(producto => !producto.es_base_equivalencia);
        }

        // Filtro de búsqueda general (con normalización)
        if (searchQuery.trim()) {
            filtered = filtered.filter(producto =>
                coincideNormalizado(producto.nombre, searchQuery) ||
                coincideNormalizado(producto.codigo_corto, searchQuery) ||
                coincideNormalizado(producto.codigo_barras, searchQuery) ||
                coincideNormalizado(producto.descripcion, searchQuery)
            );
        }

        // Filtro por categoría (selector principal)
        if (selectedCategory) {
            filtered = filtered.filter(producto =>
                producto.categoria_id === parseInt(selectedCategory)
            );
        }

        // Filtros por columna (con normalización)
        if (columnFilters.nombre) {
            filtered = filtered.filter(producto =>
                coincideNormalizado(producto.nombre, columnFilters.nombre)
            );
        }

        if (columnFilters.codigo_corto) {
            filtered = filtered.filter(producto =>
                coincideNormalizado(producto.codigo_corto, columnFilters.codigo_corto)
            );
        }

        if (columnFilters.codigo_barras) {
            filtered = filtered.filter(producto =>
                coincideNormalizado(producto.codigo_barras, columnFilters.codigo_barras)
            );
        }

        if (columnFilters.categoria) {
            filtered = filtered.filter(producto => {
                const categoriaNombre = getCategoriaName(producto.categoria_id);
                return categoriaNombre.toLowerCase().includes(columnFilters.categoria.toLowerCase());
            });
        }

        if (columnFilters.almacen) {
            filtered = filtered.filter(producto => {
                const almacenNombre = getAlmacenName(producto.almacen_id);
                return almacenNombre.toLowerCase().includes(columnFilters.almacen.toLowerCase());
            });
        }

        setFilteredProductos(filtered);
        setVisibleCount(ITEMS_PER_LOAD); // Reset al filtrar
    };

    // Productos visibles (lazy loading)
    const visibleProducts = useMemo(() => {
        return filteredProductos.slice(0, visibleCount);
    }, [filteredProductos, visibleCount]);

    const hasMore = visibleCount < filteredProductos.length;

    // Manejar scroll para cargar más
    const handleTableScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 100 && hasMore) {
            setVisibleCount(prev => Math.min(prev + ITEMS_PER_LOAD, filteredProductos.length));
        }
    }, [hasMore, filteredProductos.length]);

    const handleCreateProducto = async (productoData, descuentosData, equivalenciasData, nuevosEquivalentes = []) => {
        try {
            const result = await apiService.crearProducto(productoData);

            if (result.success) {
                const productoId = result.data.id;

                // Guardar los descuentos por volumen del producto principal
                if (descuentosData && descuentosData.length > 0) {
                    try {
                        await apiService.guardarDescuentosVolumen(productoId, descuentosData);
                    } catch (error) {
                        console.warn('No se pudieron guardar los descuentos del producto:', error);
                    }
                }

                // Crear nuevos productos equivalentes y guardar relaciones
                const todasEquivalencias = [...equivalenciasData];

                if (nuevosEquivalentes && nuevosEquivalentes.length > 0) {
                    for (const eq of nuevosEquivalentes) {
                        // Crear el producto equivalente (solo requiere unidad y equivalencia)
                        const nombreProducto = `${productoData.nombre} (${eq.label})`;
                        const codigoCorto = eq.codigo_corto || (productoData.codigo_corto ? `${productoData.codigo_corto}-${eq.unidad.toUpperCase()}` : '');

                        const nuevoProducto = {
                            nombre: nombreProducto,
                            codigo_corto: codigoCorto || null,
                            codigo_barras: eq.codigo_barras || null,
                            categoria_id: productoData.categoria_id || null,
                            almacen_id: productoData.almacen_id || null,
                            unidad_medida: eq.unidad,
                            precio_compra: eq.costo || 0,
                            precio_lista: eq.precio_lista || 0,
                            precio1: eq.precio1 || 0,
                            precio2: eq.precio2 || 0,
                            precio3: eq.precio3 || 0,
                            precio4: eq.precio4 || 0,
                            precio5: eq.precio5 || 0,
                            precio6: eq.precio6 || 0,
                            stock: 0,
                            stock_minimo: 0,
                            activo: 1,
                            es_base: 0
                        };

                        try {
                            const equivResult = await apiService.crearProducto(nuevoProducto);
                            if (equivResult.success) {
                                const equivProductoId = equivResult.data.id;

                                // Si hay volumen y descuento, guardar descuento por volumen para el producto equivalente
                                if (eq.volumen > 0 && eq.descuento > 0) {
                                    try {
                                        await apiService.guardarDescuentosVolumen(equivProductoId, [{
                                            cantidad_minima: parseFloat(eq.volumen),
                                            descuento_porcentaje: parseFloat(eq.descuento)
                                        }]);
                                    } catch (descError) {
                                        console.warn(`No se pudo guardar descuento por volumen para ${eq.label}:`, descError);
                                    }
                                }

                                // Agregar a la lista de equivalencias para guardar la relación
                                todasEquivalencias.push({
                                    producto_equivalente_id: equivProductoId,
                                    cantidad: eq.equivalencia || 1,
                                    volumen: eq.volumen || 0,
                                    descuento: eq.descuento || 0,
                                    codigo_corto: codigoCorto,
                                    codigo_barras: eq.codigo_barras || null,
                                    costo: eq.costo || 0,
                                    precio_lista: eq.precio_lista || 0,
                                    precio1: eq.precio1 || 0,
                                    precio2: eq.precio2 || 0,
                                    precio3: eq.precio3 || 0,
                                    precio4: eq.precio4 || 0,
                                    precio5: eq.precio5 || 0,
                                    precio6: eq.precio6 || 0
                                });
                            }
                        } catch (error) {
                            console.warn(`Error al crear equivalente ${eq.label}:`, error);
                        }
                    }
                }

                // Guardar todas las relaciones de equivalencia
                if (todasEquivalencias.length > 0) {
                    try {
                        // Si es_base = 1, invertir los IDs (el producto equivalente tiene producto_id, el base tiene producto_equivalente_id)
                        if (productoData.es_base === 1 || productoData.es_base === '1') {
                            // Llamar una vez por cada equivalente con IDs invertidos
                            // NO enviar precios, volumen ni descuento aquí:
                            // - Los precios del base vienen del tab de precios
                            // - El descuento por volumen del base viene del tab de descuentos
                            // - El descuento por volumen del equivalente ya se guardó al crearlo
                            for (const equiv of todasEquivalencias) {
                                const equivProductId = equiv.producto_equivalente_id;
                                const equivalenciaInvertida = [{
                                    producto_equivalente_id: productoId,  // El producto base va aquí
                                    cantidad: equiv.cantidad
                                }];
                                await apiService.guardarEquivalenciasProducto(equivProductId, equivalenciaInvertida);
                            }
                        } else {
                            // Comportamiento normal: el producto principal es producto_id
                            await apiService.guardarEquivalenciasProducto(productoId, todasEquivalencias);
                        }
                    } catch (error) {
                        console.warn('No se pudieron guardar las equivalencias del producto:', error);
                    }
                }

                setShowCreateModal(false);
                loadProductos();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al crear producto');
        }
    };

    const handleEditProducto = async (productoData, descuentosData, equivalenciasData, nuevosEquivalentes = []) => {
        try {
            const result = await apiService.actualizarProducto({
                id: selectedProducto.id,
                ...productoData
            });

            if (result.success) {
                const productoId = selectedProducto.id;

                // Actualizar los descuentos por volumen (siempre se envían)
                try {
                    await apiService.guardarDescuentosVolumen(productoId, descuentosData || []);
                } catch (error) {
                    console.warn('No se pudieron actualizar los descuentos del producto:', error);
                }

                // Crear nuevos productos equivalentes si hay
                const todasEquivalencias = [...(equivalenciasData || [])];

                if (nuevosEquivalentes && nuevosEquivalentes.length > 0) {
                    for (const eq of nuevosEquivalentes) {
                        // Crear el producto equivalente (solo requiere unidad y equivalencia)
                        const nombreProducto = `${productoData.nombre} (${eq.label})`;
                        const codigoCorto = eq.codigo_corto || (productoData.codigo_corto ? `${productoData.codigo_corto}-${eq.unidad.toUpperCase()}` : '');

                        const nuevoProducto = {
                            nombre: nombreProducto,
                            codigo_corto: codigoCorto || null,
                            codigo_barras: eq.codigo_barras || null,
                            categoria_id: productoData.categoria_id || null,
                            almacen_id: productoData.almacen_id || null,
                            unidad_medida: eq.unidad,
                            precio_compra: eq.costo || 0,
                            precio_lista: eq.precio_lista || 0,
                            precio1: eq.precio1 || 0,
                            precio2: eq.precio2 || 0,
                            precio3: eq.precio3 || 0,
                            precio4: eq.precio4 || 0,
                            precio5: eq.precio5 || 0,
                            precio6: eq.precio6 || 0,
                            stock: 0,
                            stock_minimo: 0,
                            activo: 1,
                            es_base: 0
                        };

                        try {
                            const equivResult = await apiService.crearProducto(nuevoProducto);
                            if (equivResult.success) {
                                const equivProductoId = equivResult.data.id;

                                // Si hay volumen y descuento, guardar descuento por volumen para el producto equivalente
                                if (eq.volumen > 0 && eq.descuento > 0) {
                                    try {
                                        await apiService.guardarDescuentosVolumen(equivProductoId, [{
                                            cantidad_minima: parseFloat(eq.volumen),
                                            descuento_porcentaje: parseFloat(eq.descuento)
                                        }]);
                                    } catch (descError) {
                                        console.warn(`No se pudo guardar descuento por volumen para ${eq.label}:`, descError);
                                    }
                                }

                                todasEquivalencias.push({
                                    producto_equivalente_id: equivProductoId,
                                    cantidad: eq.equivalencia || 1,
                                    volumen: eq.volumen || 0,
                                    descuento: eq.descuento || 0,
                                    codigo_corto: codigoCorto,
                                    codigo_barras: eq.codigo_barras || null,
                                    costo: eq.costo || 0,
                                    precio_lista: eq.precio_lista || 0,
                                    precio1: eq.precio1 || 0,
                                    precio2: eq.precio2 || 0,
                                    precio3: eq.precio3 || 0,
                                    precio4: eq.precio4 || 0,
                                    precio5: eq.precio5 || 0,
                                    precio6: eq.precio6 || 0
                                });
                            }
                        } catch (error) {
                            console.warn(`Error al crear equivalente ${eq.label}:`, error);
                        }
                    }
                }

                // Guardar todas las relaciones de equivalencia
                try {
                    // Si es_base = 1, invertir los IDs (el producto equivalente tiene producto_id, el base tiene producto_equivalente_id)
                    if (productoData.es_base === 1 || productoData.es_base === '1') {
                        // Llamar una vez por cada equivalente con IDs invertidos
                        // NO enviar precios, volumen ni descuento aquí:
                        // - Los precios del base vienen del tab de precios
                        // - El descuento por volumen del base viene del tab de descuentos
                        // - El descuento por volumen del equivalente ya se guardó al crearlo
                        for (const equiv of todasEquivalencias) {
                            const equivProductId = equiv.producto_equivalente_id;
                            const equivalenciaInvertida = [{
                                producto_equivalente_id: productoId,  // El producto base va aquí
                                cantidad: equiv.cantidad
                            }];
                            await apiService.guardarEquivalenciasProducto(equivProductId, equivalenciaInvertida);
                        }
                    } else {
                        // Comportamiento normal: el producto principal es producto_id
                        await apiService.guardarEquivalenciasProducto(productoId, todasEquivalencias);
                    }
                } catch (error) {
                    console.warn('No se pudieron actualizar las equivalencias del producto:', error);
                }

                setShowEditModal(false);
                setSelectedProducto(null);
                loadProductos();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al actualizar producto');
        }
    };

    const handleDeleteProducto = async (id, nombre) => {
        if (!window.confirm(`¿Estás seguro de que quieres desactivar el producto "${nombre}"?\n\nNota: El producto se ocultará de la lista pero se mantendrá en el historial.`)) {
            return;
        }

        try {
            const result = await apiService.eliminarProducto(id);

            if (result.success) {
                loadProductos();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al desactivar producto');
        }
    };

    const handleBulkPrinterAssignment = async (productsToUpdate) => {
        try {
            setLoading(true);
            clearError();

            const result = await apiService.actualizarImpresorasMasivo(productsToUpdate);

            if (result.success) {
                loadProductos();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al asignar impresoras');
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const getCategoriaName = (categoriaId) => {
        const categoria = categorias.find(c => c.id === categoriaId);
        return categoria ? categoria.nombre : 'Sin categoría';
    };

    const getAlmacenName = (almacenId) => {
        if (!almacenId) return 'Sin almacén';
        const almacen = almacenes.find(a => a.id === almacenId);
        return almacen ? almacen.nombre : 'Sin almacén';
    };

    const getStockStatus = (stock) => {
        if (stock <= 5) return 'low';
        if (stock <= 10) return 'medium';
        return 'high';
    };

    return (
        <div className="product-management-page">


            {/* Pestañas */}
            <div className="product-tabs">
                <button
                    className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
                    onClick={() => setActiveTab('products')}
                >
                    <Package size={18} />
                    Productos
                </button>
                <button
                    className={`tab-button ${activeTab === 'categories' ? 'active' : ''}`}
                    onClick={() => setActiveTab('categories')}
                >
                    <Tag size={18} />
                    Categorías
                </button>
            </div>

            {/* Contenido de las pestañas */}
            <div className="product-content">
                {activeTab === 'products' && (
                    <>
                        {/* Controles */}
                        <div className="product-controls">
                            <div className="search-filters">
                                <div className="search-container">
                                    <Input
                                        ref={searchInputRef}
                                        placeholder="Buscar por nombre, código corto, código de barras o descripción..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        icon={<Search size={20} />}
                                        className="search-input"
                                    />
                                </div>

                                <div className="filter-container">
                                    <select
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                        className="category-filter"
                                    >
                                        <option value="">Todas las categorías</option>
                                        {categorias.map(categoria => (
                                            <option key={categoria.id} value={categoria.id}>
                                                {categoria.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="controls-actions">
                                <label className="toggle-equivalencia" title="Mostrar solo productos con mayor equivalencia (ocultar productos base)">
                                    <input
                                        type="checkbox"
                                        checked={soloMayorEquivalencia}
                                        onChange={(e) => setSoloMayorEquivalencia(e.target.checked)}
                                    />
                                    <span className="toggle-equiv-slider"></span>
                                    <span className="toggle-label">Mayor equiv.</span>
                                </label>
                                {hasActiveFilters && (
                                    <Button
                                        variant="outline"
                                        onClick={clearAllFilters}
                                        className="clear-filters-btn"
                                    >
                                        Limpiar filtros
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    onClick={() => setShowImportModal(true)}
                                    disabled={loading}
                                >
                                    Importar
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => setShowPrinterModal(true)}
                                    disabled={loading || productos.length === 0}
                                >
                                    Impresoras
                                </Button>
                                <Button
                                    onClick={() => setShowCreateModal(true)}
                                    disabled={loading}
                                >
                                    Nuevo Producto
                                </Button>
                            </div>
                        </div>


                        {/* Tabla de productos */}
                        <div
                            className="products-table-container"
                            ref={tableContainerRef}
                        >
                            {loading && (
                                <div className="loading-state">
                                    <div className="loading-spinner"></div>
                                    <p>Cargando productos...</p>
                                </div>
                            )}

                            {!loading && filteredProductos.length === 0 && (
                                <div className="empty-state">
                                    <Package size={48} />
                                    <h3>No se encontraron productos</h3>
                                    <p>
                                        {searchQuery || selectedCategory
                                            ? 'No hay productos que coincidan con los filtros'
                                            : 'No hay productos registrados'
                                        }
                                    </p>
                                    {!searchQuery && !selectedCategory && (
                                        <Button
                                            onClick={() => setShowCreateModal(true)}
                                            className="empty-action-btn"
                                        >

                                            Crear primer producto
                                        </Button>
                                    )}
                                </div>
                            )}

                            {!loading && filteredProductos.length > 0 && (
                                <>
                                    <div className="products-count-info">
                                        Mostrando {visibleProducts.length} de {filteredProductos.length} productos
                                    </div>
                                    <div className="products-table-wrapper" onScroll={handleTableScroll}>
                                        <table className="products-table">
                                            <thead>
                                                <tr className="header-row">
                                                    <th>Producto</th>
                                                    <th>Cód. Corto</th>
                                                    <th>Cód. Barras</th>
                                                    <th>Categoría</th>
                                                    <th>Almacén</th>
                                                    <th>Costo</th>
                                                    <th>P1</th>
                                                    <th>P2</th>
                                                    <th>P3</th>
                                                    <th>P4</th>
                                                    <th>Stock</th>
                                                    <th>Acciones</th>
                                                </tr>
                                                <tr className="filter-row">
                                                    <th>
                                                        <input
                                                            type="text"
                                                            className="column-filter-input"
                                                            placeholder="Filtrar..."
                                                            value={columnFilters.nombre}
                                                            onChange={(e) => handleColumnFilterChange('nombre', e.target.value)}
                                                        />
                                                    </th>
                                                    <th>
                                                        <input
                                                            type="text"
                                                            className="column-filter-input"
                                                            placeholder="Filtrar..."
                                                            value={columnFilters.codigo_corto}
                                                            onChange={(e) => handleColumnFilterChange('codigo_corto', e.target.value)}
                                                        />
                                                    </th>
                                                    <th>
                                                        <input
                                                            type="text"
                                                            className="column-filter-input"
                                                            placeholder="Filtrar..."
                                                            value={columnFilters.codigo_barras}
                                                            onChange={(e) => handleColumnFilterChange('codigo_barras', e.target.value)}
                                                        />
                                                    </th>
                                                    <th>
                                                        <input
                                                            type="text"
                                                            className="column-filter-input"
                                                            placeholder="Filtrar..."
                                                            value={columnFilters.categoria}
                                                            onChange={(e) => handleColumnFilterChange('categoria', e.target.value)}
                                                        />
                                                    </th>
                                                    <th>
                                                        <input
                                                            type="text"
                                                            className="column-filter-input"
                                                            placeholder="Filtrar..."
                                                            value={columnFilters.almacen}
                                                            onChange={(e) => handleColumnFilterChange('almacen', e.target.value)}
                                                        />
                                                    </th>
                                                    <th></th>
                                                    <th></th>
                                                    <th></th>
                                                    <th></th>
                                                    <th></th>
                                                    <th></th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {visibleProducts.map((producto) => (
                                                    <tr key={producto.id}>
                                                        <td>
                                                            <div className="product-name">{producto.nombre}</div>
                                                        </td>
                                                        <td>{producto.codigo_corto || '-'}</td>
                                                        <td>
                                                            <div className="barcode-info">
                                                                <Barcode size={14} />
                                                                {producto.codigo_barras || '-'}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="category-badge">
                                                                <Tag size={12} />
                                                                {getCategoriaName(producto.categoria_id)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="category-badge">
                                                                {getAlmacenName(producto.almacen_id)}
                                                            </span>
                                                        </td>
                                                        <td className="price">{formatMoney(producto.precio_compra)}</td>
                                                        <td className="price">{formatMoney(producto.precio1 || 0)}</td>
                                                        <td className="price">{formatMoney(producto.precio2 || 0)}</td>
                                                        <td className="price">{formatMoney(producto.precio3 || 0)}</td>
                                                        <td className="price">{formatMoney(producto.precio4 || 0)}</td>
                                                        <td>
                                                            <span className={`stock-badge ${getStockStatus(producto.stock)}`}>
                                                                {producto.stock} {producto.unidad_medida || 'pz'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="actions-compact">
                                                                <button
                                                                    className="action-btn-compact"
                                                                    onClick={() => {
                                                                        setSelectedProducto(producto);
                                                                        setShowEditModal(true);
                                                                    }}
                                                                    title="Editar"
                                                                >
                                                                    <Edit size={14} />
                                                                </button>
                                                                <button
                                                                    className="action-btn-compact delete"
                                                                    onClick={() => handleDeleteProducto(producto.id, producto.nombre)}
                                                                    title="Desactivar"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {hasMore && (
                                        <div className="loading-more">
                                            Desplaza para cargar más productos...
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}

                {activeTab === 'categories' && (
                    <CategoriesTab
                        categorias={categorias}
                        onCategoriesChange={loadCategorias}
                    />
                )}
            </div>

            {/* Modales */}
            {showCreateModal && (
                <Modal
                    title="Crear Nuevo Producto"
                    onClose={() => setShowCreateModal(false)}
                    size="fullscreen"
                    headerActions={headerToggle}
                >
                    <ProductForm
                        categorias={categorias}
                        almacenes={almacenes}
                        onSubmit={handleCreateProducto}
                        onCancel={() => setShowCreateModal(false)}
                        isLoading={loading}
                        renderHeaderToggle={setHeaderToggle}
                    />
                </Modal>
            )}

            {showEditModal && selectedProducto && (
                <Modal
                    title="Editar Producto"
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedProducto(null);
                    }}
                    size="fullscreen"
                    headerActions={headerToggle}
                >
                    <ProductForm
                        producto={selectedProducto}
                        categorias={categorias}
                        almacenes={almacenes}
                        onSubmit={handleEditProducto}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedProducto(null);
                        }}
                        isLoading={loading}
                        isEdit={true}
                        renderHeaderToggle={setHeaderToggle}
                    />
                </Modal>
            )}

            {showImportModal && (
                <ImportProductsModal
                    onClose={() => setShowImportModal(false)}
                    onImportComplete={() => {
                        setShowImportModal(false);
                        loadProductos();
                    }}
                    categorias={categorias}
                    almacenes={almacenes}
                />
            )}

            {showPrinterModal && (
                <PrinterAssignmentModal
                    productos={productos}
                    onClose={() => setShowPrinterModal(false)}
                    onSave={handleBulkPrinterAssignment}
                />
            )}

        </div>
    );
};

export default ProductManagementPage;